import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Appointment } from "../../../types";
import { appointmentsApi } from "../../../services";
import { getTodayInBrazil } from "../../../utils/dateUtils";
import { useCompany } from "../../../contexts/CompanyContext";
import { ConfirmationModal, useToaster } from "../../shared";
import { Icons } from "../../shared/Icons";
import { RescheduleAppointmentModal } from "../AdminAppointments/RescheduleAppointmentModal";
import { AdminPageHeader } from "../shared";
import { WeekAppointmentRemindersPanel } from "./WeekAppointmentRemindersPanel";
import { WeekPeriodPicker } from "./WeekPeriodPicker";

export const AdminWeekAppointmentRemindersPage: React.FC = () => {
  const navigate = useNavigate();
  const { company } = useCompany();
  const { showToast } = useToaster();
  const isCarCompany = company?.category === "car";

  const [referenceDate, setReferenceDate] = useState(() => getTodayInBrazil());
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingIds, setMarkingIds] = useState<Set<number>>(() => new Set());
  const [appointmentToEdit, setAppointmentToEdit] = useState<Appointment | null>(
    null
  );
  const [appointmentToCancel, setAppointmentToCancel] =
    useState<Appointment | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    if (company && !isCarCompany) {
      navigate("/admin/daily-cash", { replace: true });
    }
  }, [company, isCarCompany, navigate]);

  const loadWeekAppointments = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await appointmentsApi.getWeekAppointments(referenceDate);
      if (res.success && res.data) {
        setAppointments(res.data);
      } else {
        setAppointments([]);
        setError(res.error || "Erro ao carregar agendamentos da semana");
      }
    } catch (e) {
      setAppointments([]);
      setError(
        e instanceof Error
          ? e.message
          : "Erro ao carregar agendamentos da semana"
      );
    } finally {
      setLoading(false);
    }
  }, [referenceDate]);

  useEffect(() => {
    if (isCarCompany) {
      loadWeekAppointments();
    }
  }, [isCarCompany, loadWeekAppointments]);

  const handleWhatsAppReminder = async (appointment: Appointment) => {
    if (appointment.confirmationSent) {
      return;
    }

    setMarkingIds((prev) => new Set(prev).add(appointment.id));

    try {
      const res = await appointmentsApi.markConfirmationSent(appointment.id);
      if (res.success) {
        setAppointments((prev) =>
          prev.map((item) =>
            item.id === appointment.id
              ? { ...item, confirmationSent: true }
              : item
          )
        );
      } else {
        showToast(
          "error",
          res.error || "Não foi possível registrar o lembrete enviado"
        );
      }
    } catch (e) {
      showToast(
        "error",
        e instanceof Error
          ? e.message
          : "Não foi possível registrar o lembrete enviado"
      );
    } finally {
      setMarkingIds((prev) => {
        const next = new Set(prev);
        next.delete(appointment.id);
        return next;
      });
    }
  };

  const handleCancelConfirm = async () => {
    if (!appointmentToCancel) return;

    setCancelLoading(true);
    try {
      const res = await appointmentsApi.cancelAppointment(appointmentToCancel.id);
      if (res.success) {
        showToast("success", "Agendamento cancelado com sucesso");
        setAppointmentToCancel(null);
        await loadWeekAppointments();
      } else {
        showToast("error", res.error || "Erro ao cancelar agendamento");
      }
    } catch (e) {
      showToast(
        "error",
        e instanceof Error ? e.message : "Erro ao cancelar agendamento"
      );
    } finally {
      setCancelLoading(false);
    }
  };

  if (!isCarCompany) {
    return null;
  }

  const pendingCount = appointments.filter(
    (a) =>
      !a.confirmationSent && a.status?.toLowerCase() !== "completed"
  ).length;

  return (
    <div className="space-y-4 sm:space-y-6 md:space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<Icons.Event sx={{ fontSize: 28 }} />}
        title="Lembretes da Semana"
        subtitle={
          loading
            ? "Carregando agendamentos da semana…"
            : appointments.length > 0
              ? `${pendingCount} ${
                  pendingCount === 1
                    ? "agendamento precisa"
                    : "agendamentos precisam"
                } de lembrete nesta semana.`
              : "Envie lembretes de confirmação aos clientes com agendamento nesta semana."
        }
      />

      <WeekPeriodPicker
        referenceDate={referenceDate}
        onChange={setReferenceDate}
        disabled={loading}
      />

      <WeekAppointmentRemindersPanel
        appointments={appointments}
        loading={loading}
        error={error}
        companyName={company?.name || "nossa empresa"}
        markingIds={markingIds}
        onRetry={loadWeekAppointments}
        onWhatsAppReminder={handleWhatsAppReminder}
        onEditAppointment={setAppointmentToEdit}
        onCancelAppointment={setAppointmentToCancel}
      />

      {appointmentToEdit && (
        <RescheduleAppointmentModal
          isOpen={Boolean(appointmentToEdit)}
          appointment={appointmentToEdit}
          onClose={() => setAppointmentToEdit(null)}
          onSuccess={() => {
            setAppointmentToEdit(null);
            showToast("success", "Agendamento reagendado com sucesso");
            loadWeekAppointments();
          }}
        />
      )}

      <ConfirmationModal
        isOpen={Boolean(appointmentToCancel)}
        title="Cancelar Agendamento"
        message={`Tem certeza que deseja cancelar o agendamento de ${appointmentToCancel?.client.name}? Esta ação não pode ser desfeita.`}
        confirmText="Cancelar Agendamento"
        cancelText="Voltar"
        loading={cancelLoading}
        onConfirm={handleCancelConfirm}
        onCancel={() => setAppointmentToCancel(null)}
      />
    </div>
  );
};
